import { useMemo, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Button, ReferenceCombobox } from '../../components/ui'
import type { ReferenceOption } from '../../components/ui'

const inputCls =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ' +
  'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ' +
  'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 ' +
  'dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-800'

export interface CharacteristicValueItem {
  value: string
  valueId?: number | string | null
}

export interface CharacteristicValueInputProps {
  id?: string
  'aria-label'?: string
  values: CharacteristicValueItem[]
  onChange: (values: CharacteristicValueItem[]) => void
  /** Коллекция значений. Для одиночного значения рендерится обычное поле или combobox. */
  multiple?: boolean
  /** Предел площадки. `null`, `undefined` и 0 означают отсутствие известного предела. */
  maxCount?: number | null
  /** Наличие options переводит компонент в словарный режим. */
  options?: ReferenceOption[]
  onSearch?: (query: string) => void
  loading?: boolean
  placeholder?: string
  emptyText?: string
  inputType?: 'text' | 'number'
  numberStep?: number | 'any'
  multiline?: boolean
  disabled?: boolean
}

function normalizedLimit(maxCount: number | null | undefined): number | null {
  const value = Number(maxCount)
  return Number.isInteger(value) && value > 0 ? value : null
}

function itemKey(item: CharacteristicValueItem): string {
  return `${item.valueId ?? ''}:${item.value}`
}

/**
 * Единый ввод характеристики площадки.
 *
 * Компонент намеренно принимает массив значений и ничего не знает о формате
 * хранения конкретного экрана. Форма категории сериализует массив через `;`,
 * а карточка товара сможет передать свой массив override-значений без второй
 * реализации чипов, словаря и предела площадки.
 */
export function CharacteristicValueInput({
  id,
  'aria-label': ariaLabel,
  values,
  onChange,
  multiple = false,
  maxCount,
  options,
  onSearch,
  loading = false,
  placeholder,
  emptyText,
  inputType = 'text',
  numberStep = 'any',
  multiline = false,
  disabled = false,
}: CharacteristicValueInputProps) {
  const [pending, setPending] = useState('')
  const [limitError, setLimitError] = useState('')
  const limit = normalizedLimit(maxCount)
  const limitReached = multiple && limit !== null && values.length >= limit
  const dictionary = options !== undefined

  const selectedKeys = useMemo(
    () => new Set(values.map((item) => itemKey(item))),
    [values],
  )

  const removeAt = (index: number) => {
    setLimitError('')
    onChange(values.filter((_item, itemIndex) => itemIndex !== index))
  }

  const addFreeValues = () => {
    const additions = pending
      .split(';')
      .map((value) => value.trim())
      .filter(Boolean)
      .filter((value, index, list) => list.indexOf(value) === index)
      .filter((value) => !values.some((item) => item.value === value))

    if (additions.length === 0) {
      setPending('')
      return
    }
    if (limit !== null && values.length + additions.length > limit) {
      setLimitError(`Можно выбрать не больше ${limit}`)
      return
    }

    setLimitError('')
    onChange([...values, ...additions.map((value) => ({ value, valueId: null }))])
    setPending('')
  }

  if (!multiple && dictionary) {
    const current = values[0]
    const currentCode = current
      ? String(current.valueId ?? current.value)
      : null
    return (
      <ReferenceCombobox
        id={id}
        aria-label={ariaLabel}
        value={currentCode}
        selectedLabel={current?.value}
        onChange={(selected) => {
          if (!selected) {
            onChange([])
            return
          }
          const option = options.find((item) => item.value === selected)
          onChange([{
            value: option?.label ?? selected,
            valueId: option?.value ?? null,
          }])
        }}
        options={options}
        onSearch={onSearch}
        loading={loading}
        placeholder={placeholder}
        emptyText={emptyText}
        disabled={disabled}
      />
    )
  }

  if (!multiple) {
    return multiline
      ? (
        <textarea
          id={id}
          aria-label={ariaLabel}
          className={inputCls}
          value={values[0]?.value ?? ''}
          disabled={disabled}
          placeholder={placeholder ?? 'Введите значение'}
          onChange={(event) => onChange([{ value: event.target.value, valueId: null }])}
          rows={4}
        />
      )
      : (
        <input
          id={id}
          aria-label={ariaLabel}
          className={inputCls}
          value={values[0]?.value ?? ''}
          disabled={disabled}
          placeholder={placeholder ?? 'Введите значение'}
          onChange={(event) => onChange([{ value: event.target.value, valueId: null }])}
          type={inputType}
          step={inputType === 'number' ? numberStep : undefined}
        />
      )
  }

  const availableOptions = options?.filter((option) => (
    !selectedKeys.has(`${option.value}:${option.label}`)
    && !values.some((item) => item.value === option.label)
  ))

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((item, index) => (
            <span
              key={`${itemKey(item)}:${index}`}
              className="inline-flex items-center gap-1 rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300"
            >
              {item.value}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Убрать «${item.value}»`}
                  onClick={() => removeAt(index)}
                  className="rounded text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {dictionary ? (
        <ReferenceCombobox
          id={id}
          aria-label={ariaLabel}
          value={null}
          onChange={(selected) => {
            if (!selected || limitReached) return
            const option = options.find((item) => item.value === selected)
            const next = {
              value: option?.label ?? selected,
              valueId: option?.value ?? null,
            }
            if (!values.some((item) => item.value === next.value)) {
              setLimitError('')
              onChange([...values, next])
            }
          }}
          options={availableOptions ?? []}
          onSearch={onSearch}
          loading={loading}
          placeholder={limitReached ? 'Достигнут предел значений' : placeholder}
          emptyText={emptyText}
          disabled={disabled || limitReached}
        />
      ) : (
        <div className="flex items-start gap-2">
          <input
            id={id}
            aria-label={ariaLabel}
            type={inputType}
            step={inputType === 'number' ? numberStep : undefined}
            className={inputCls}
            value={pending}
            onChange={(event) => {
              setPending(event.target.value)
              setLimitError('')
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              addFreeValues()
            }}
            placeholder={limitReached ? 'Достигнут предел значений' : (placeholder ?? 'Введите значение')}
            disabled={disabled || limitReached}
          />
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={addFreeValues}
            disabled={disabled || limitReached || !pending.trim()}
          >
            Добавить
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className={limitError ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-zinc-400'}>
          {limitError || (dictionary
            ? 'Выберите значение из словаря'
            : 'Введите значение и нажмите Enter или «Добавить»')}
        </span>
        {limit !== null && (
          <span className="font-medium text-gray-600 dark:text-zinc-300">
            Выбрано {values.length} из {limit}
          </span>
        )}
      </div>
    </div>
  )
}

export default CharacteristicValueInput
